<template>
  <div ref="tabsContainer" class="class-area" :class="{'ios-fix' : isTabFix}">
    <!-- MY CLASSES VIEW -->
    <div v-if="emptyClassList" class="class-empty-modal">
      <h1 class="class-empty-modal_heading">Create Your First Class</h1>
      <p class="class-empty-modal_text">There are no classes in session. Start with creating your first class and learn more about the process.</p>
      <button @click="createNewClass()" class="button is-primary class-empty-modal_button">START</button>
    </div>
    <div v-if="!showClass && !emptyClassList" class="class-options">
      <button @click="createNewClass()"
              class="button createClassBtn"
              tabindex="0"
              qa="new-class">
        <span class="createClassBtn_text display-on-mobile"><i class="far fa-plus"></i></span>
        <span class="createClassBtn_text hide-on-mobile"><i class="far fa-plus mr-2"></i>{{ $t('teacherRegistration.addClass') }}</span>
      </button>
    </div>
    <div v-if="!showClass && !emptyClassList" class="is-centered">
      <div class="heading-container">
        <h1 class="classes-heading">My Classes</h1>
      </div>
      <div class="class-list_container">

      </div>
      <ul class="class-list">
        <class-card v-for="classCard in classList"
                    :key="classCard.id"
                    :card="classCard"
                    :classImage="classCard.image"
                    class="classCard"
                    @settingsDropdown="showEditClassModal(classCard)"
                    @selectClass="selectedClass(classCard)" />
      </ul>
    </div>
    <div v-if="showClass" class="class-view">
      <div class="select">
        <select v-model="showStudentsOrProjects" class="class-view-select">
          <option value="projects">View By Projects</option>
          <option value="users">View By Students</option>
        </select>
      </div>
    </div>
    <!-- CLASS PROJECTS/USERS VIEW -->
    <div v-if="showClass">
      <div>
        <div class="heading-container">
          <div v-if="showClass" class="back-button-container">
            <a @click="showMyClasses()"><i class="fa-light fa-chevron-left back-button"></i></a>
          </div>
          <h1 v-if="!editing" class="classes-heading mb-2">{{ className }}</h1>
          <p class="class-settings-button-text">Edit Class</p>
          <button v-if="!editing" @click="showEditClassModal()" class="class-settings-button">
            <i class="fal fa-pencil-alt" />
          </button>
        </div>
        <p class="heading-container mb-2">
          Class Code:
          <span @click="copyText()" class="class-code" ref="classCode">{{ classId }}<button @click="copyText()" class="class-settings-button mx-1"><i class="fa-light fa-clone"></i></button></span>
        </p>
        <p v-show="showCopyTextMessage" class="copyTextMessage">copied to clipboard</p>
        <div>
          <div v-if="showClass" class="back-button-container_mobile">
            <a @click="showMyClasses()"><i class="fa-light fa-chevron-left back-button"></i></a>
          </div>
        </div>
      </div>
      <div v-show="showUsers">
        <class-user-card-list :user="user"
                              :selectedClass="selectedClassList"
                              @addNewStudent="addNewStudent"/>
      </div>
      <div v-if="showProjects && emptyProjectList" class="class-empty-modal">
        <h1 class="class-empty-modal_heading">There Are No Projects To Review Yet</h1>
        <p class="class-empty-modal_text">Make sure you send the Unique Class Code to your students, to get them started on their projects. Once they register, their projects will show up here.</p>
      </div>
      <div v-show="showProjects" class="project-cards-container">
        <infinite-scroll :callback="getGroupProjects" distance="5">
          <div class="project-container" v-for="project in selectedClassProjects" :key="project.id">
            <group-project @click.native="goToSelectedProject(project)"
                           :projectID="project.id"
                           :key="project.id"
                           :showFollow="false"
                           :linkStyles="true"
                           :isGroupProject="true"
                           :milestonePostCount="project.milestonePostCount"
                           :completedPostCount="project.completedPostCount"
                           :cardHeading="project.user.firstName + ' ' + project.user.lastName"/>
          </div>
        </infinite-scroll>
      </div>
    </div>
    <loader :loading="loading" />
    <add-new-student v-if="showAddNewStudent"
                     :addNewStudentByTeacher="addNewStudentByTeacher"
                     :classId="classId"
                     :selectedClass="selectedClassList"
                     @closeCreateStudent="closeCreateStudent" />
    <show-teacher-code v-if="showTeacherCodeModal"
                       @closeTeacherCodeModal="closeTeacherCodeModal"
                       :selectedClass="selectedClassList"
                       :createClass="createClass"
                       :updateClass="updateClass" />
  </div>
</template>

<script>
import breakpoints from '@/libs/breakpoints'
import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import ClassUserCardList from '@/components/Pages/ManageClasses/Tab/ClassUserCardList'
import AddNewStudent from '@/components/Modals/StudentSignUpByCode'
import ShowTeacherCode from '@/components/Modals/TeacherCode'
import i18n from '@/i18n'
import ClassCard from '@/components/Cards/ClassCard'
import GroupProject from '@/components/Cards/ProjectCards/Types/GroupProject'
import InfiniteScroll from '@/components/Controls/InfiniteScroll'
import Loader from '@/components/Base/Loader/loader'

export default {
  components: {
    ClassUserCardList,
    AddNewStudent,
    ShowTeacherCode,
    i18n,
    ClassCard,
    GroupProject,
    InfiniteScroll,
    Loader
  },
  mixins: [auth, componentState],
  props: ['user', 'profile'],
  data: function () {
    return {
      isTabFix: false,
      savedTabSelection: null,
      selectedClassList: {},
      selectedClassProjects: [],
      className: '',
      showClass: false,
      showStudentsOrProjects: 'projects',
      showAddNewStudent: false,
      editStudentByTeacher: true,
      addNewStudentByTeacher: true,
      classId: 0,
      showTeacherCodeModal: false,
      updateClass: true,
      createClass: false,
      newClass: {},
      showEditIcons: false,
      editing: false,
      waiting: false,
      classIdToUpdateName: '',
      error: '',
      showCopyTextMessage: false,
      limit: 9,
      offset: 0,
      total: 0,
      hasMore: true,
      loading: false
    }
  },
  computed: {
    counts: function () {
      return this.profile ? this.profile.counts || {} : {}
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    classList: function () {
      return this.$store.getters.teacherClasses
    },
    studentList: function () {
      return this.$store.getters.studentList
    },
    emptyClassList: function () {
      return this.classList.length === 0 && !this.loading
    },
    emptyProjectList: function () {
      return this.selectedClassProjects.length === 0 && !this.loading
    },
    showUsers: function () {
      return this.showStudentsOrProjects === 'users'
    },
    showProjects: function () {
      return this.showStudentsOrProjects === 'projects'
    },
    getUserFirstLastName: function () {
      return this.user.firstName + ' ' + this.user.lastName
    }
  },
  mounted: function () {
    const activateClass = this.$route.query.classId
    if (activateClass !== undefined) {
      this.classList.forEach((classObj) => {
        if (classObj.code === activateClass) {
          this.selectedClassList = classObj
          this.selectedClass(classObj)
        }
      })
    }

    this.getClassesForTeacher()
    window.addEventListener('scroll', this.shouldFixTabBoard)
  },
  destroyed: function () {
    window.removeEventListener('scroll', this.shouldFixTabBoard)
  },
  methods: {
    copyText: function () {
      navigator.clipboard.writeText(this.$refs.classCode.innerText)
      this.showCopyTextMessage = true
      setTimeout(() => {
        this.showCopyTextMessage = false
      }, 5000)
    },
    showMyClasses: function () {
      this.selectedClassList = {}
      this.selectedClassProjects = []
      this.className = ''
      this.classId = ''
      this.showClass = false
      this.showStudentsOrProjects = 'projects'
      this.offset = 0
      this.total = 0
      this.hasMore = true
      this.loading = false
    },
    goToSelectedProject: function (project) {
      this.$router.push({ name: 'viewProject', params: { id: project.id }, query: { classId: `${this.classId}` } })
    },
    getClassesForTeacher: function () {
      this.loading = true
      this.$store.dispatch('getClassesForTeacher', this.currentUser.id).then(() => {
        this.loading = false
      }, (error) => {
        this.errorMessage = error
      })
    },
    selectedClass: function (classObject) {
      this.selectedClassList = classObject
      this.className = classObject.name
      this.classId = classObject.code
      this.showClass = true
      this.getGroupProjects()
    },
    getGroupProjects: function () {
      if (this.hasMore && !this.loading) {
        this.loading = true
        const params = {
          groupCode: this.selectedClassList.code,
          limit: this.limit,
          offset: this.offset,
          total: this.total
        }
        this.$store.dispatch('getProjectsByGroupCode', params).then((response) => {
          this.selectedClassProjects.push.apply(this.selectedClassProjects, response.projects)
          this.total = response.total
          this.hasMore = response.hasMore
          this.loading = false
          this.offset += this.limit
        })
      }
    },
    addNewStudent: function () {
      this.showAddNewStudent = true
    },
    closeCreateStudent: function () {
      this.showAddNewStudent = false
    },
    closeTeacherCodeModal: function () {
      this.showTeacherCodeModal = false
    },
    showEditClassModal: function (classObject) {
      this.createClass = false
      this.updateClass = true
      this.showTeacherCodeModal = true
      if (classObject) {
        this.selectedClassList = classObject
      }
    },
    createNewClass: function () {
      this.updateClass = false
      this.createClass = true
      this.showTeacherCodeModal = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

::v-deep .story-card-container {
  display: inline-block;
}

.class-list {
  max-width: 940px;
  margin: auto;
  @include touch {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }
}

.class-code {
  display: inline-block;
  cursor: pointer;
}

.copyTextMessage {
  max-width: 940px;
  margin: auto;
  color: green;
  font-size: 0.75rem;
  line-height: 0;
  position: relative;
  top: -3px;
}

.class-empty-modal {
  font-family: cera-pro;
  max-width: 60rem;
  margin: 3rem auto;
  padding: 3rem 12rem;
  background-color: white;
  text-align: center;
  border-radius: 2rem;
  box-shadow: 0px 2px 3px 1px #d2d2d2;
  @include touch {
    padding: 2rem 6rem;
  }
  @include mobile {
    padding: 1rem 1.3rem;
  }
  &_heading {
    font-size: 1.8rem;
    font-weight: 800;
    margin: 0.75rem 0;
    @include mobile {
      font-size: 1.3rem;
    }
  }
  &_text {
    margin: 0.75rem 0;
    font-size: 1.2rem;
    line-height: 1.8rem;
    @include mobile {
      font-size: 1rem;
    }
  }
  &_button {
    margin: 0.75rem;
    padding: 1.6rem 2.8rem;
  }
}

.back-button {
  border-radius: 50%;
  background: white;
  color: black;
  padding: 0.75rem 0.9rem;
  cursor: pointer;
  &-container {
    position: absolute;
    left: -7%;
    top: 0.25rem;
    background-color: transparent;
    @media (max-width: 1125px) {
      display: none;
    }
    &_mobile {
      display: none;
      @media (max-width: 1125px) {
        display: inline-block;
      }
    }
  }
}

.back-button-mobile-text {
  display: none;
  @include mobile {
    display: inline-block;
  }
}

.class-settings-button {
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  background: #F0F0F0;
  border: none;
  cursor: pointer;
  &:hover {
    background: white;
  }
}

::v-deep .project-container {
  display: inline-block;
  margin: 0.25rem;
}

.project-cards-container {
  max-width: 950px;
  margin: auto;
  @include touch {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }
}

.class-settings-button-text {
  display: none;
  @include mobile {
    display: inline-block;
  }
}

.classes-heading {
  font-family: cera-pro;
  font-weight: 700;
  font-size: 1.15rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid black;
  display: inline-block;
  margin: 0.6rem 0 2rem 0;
  @include mobile {
    margin: 0.85rem 0 !important;
    width: fit-content;
    display: block;
  }
}

.heading-container {
  max-width: 940px;
  margin: auto;
  position: relative;
}

.classCard {
  display: inline-block;
  margin: 0.75rem;
}

.dropdownStudent {
  width: 12rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.editBtn {
  width: 12rem;
  background: transparent;
  border-color: $primary;
  margin-bottom: 0.5rem;
}

.classInput {
  font-size: 1.15rem;
  border: none;
  //padding: 1rem;
  text-align: center;
}

.class-area {
  position: relative;
  margin: 1.5rem 0 0 0;
  @include mobile {
    margin: 0.5rem 0;
    flex-direction: column;
  }

  ul {
    border-bottom-width: 1px;
    transition: 0.25s all;
    a {
      position:relative;
      span{
        margin-right:1px;
        font-size: 0.9rem;
      }
    }
  }
}

.ios-fix{
  overflow:hidden;
}

.addStudentBtn {
  top: 30% !important;
}

.createClassBtn {
  position: absolute;
  top: 0;
  right: 0;
  width: 12rem;
  background-color: #005BAB;
  border-radius: 0;
  &_text {
    font-family: cera-pro;
    color: white;
    font-size: 1rem;
    text-transform: uppercase;
    i {
      font-size: 0.75rem;
    }
  }
}
@media (max-width: 450px) {
  .createClassBtn {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background-color: #005BAB;
    &_text {
      font-family: cera-pro;
      color: white;
      font-size: 0.75rem;
      text-transform: uppercase;
      i {
        font-size: 1rem;
      }
    }
  }
}

.adjust-length {
  height: auto;
  margin-top: 0.3rem;
}

.class-options {
  position: absolute;
  right: 12%;
  top: 0;
  z-index: 2;
  color: $grey-dark;
  letter-spacing: .05rem;
  @include mobile {
    position: relative;
    right: 0;
    margin-left: auto;
    width: 12rem;
  }
  button {
    margin: 0.3rem 0 0.3rem 0;
  }
  a {
    width: 130px;
    height: 30px;
    font-size: 0.65rem;
    border-radius: 0;
    @include mobile {
      width: 80px;
    }
  }
}

.class-view {
  position: absolute;
  right: 5%;
  z-index: 2;
  @include touch {
    position: relative;
    margin-left: auto;
    width: 12rem;
  }
}

.adjust-message {
  display: flex;
  padding-top: 5.5rem;
  padding-left: 8rem;
  justify-content: center;
  margin: auto;
  @include mobile {
    order: 1;
  }
}
.class-container {
  padding-bottom: 2rem;
}
.dropdown-container {
  padding-bottom: 1rem;
}
.edit-controls {
  display: block;
  @include mobile {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    .dropdown-container {
      width: 100%;
      display: flex;
      justify-content: center;
    }
    .class-container, .students-container {
      margin-right: 1rem;
    }
  }
}

.display-on-mobile {
  @media (min-width: 450px) {
    display: none
  }
}
.hide-on-mobile {
  @media (max-width: 450px) {
    display: none
  }
}
.adjust-bottom {
  @media (max-width: 450px) {
    margin-bottom: 1rem;
  }
}
.title-class-student {
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
}
.edit-new-button {
  font-size: 1.06rem;
  font-weight: 400;
  font-family: BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif;
}
</style>
